import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionProps,
  Box,
  Grid,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
  makeStyles,
  withStyles,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import {
  PropertyData,
  StyledAccordionDetails,
  StyledAccordionSummary,
} from "components/EmailEventDetails/styled";
import EmailDetails from "./EmailDetails";
import RuleDetails from "./RuleDetails";
import PlatformDetails from "./PlatformDetails";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { NotificationEvent } from "@dashboard-v3/api";
import StorageDetails from "./StorageDetails";

interface ItemProps {
  notification: NotificationEvent;
}

export default function NotificationItem({ notification }: ItemProps) {
  const { t } = useTranslation("notifications");
  const classes = useStyles();
  const [expanded, setExpanded] = useState<boolean>(false);
  const { createdAt, id, level, type } = notification;

  // Hasta MXH-5748 la regla era por tipo ("todos menos STORAGE"), porque STORAGE
  // era el unico sin detalle. Ahora que tiene `data` la regla mira el contenido,
  // con una excepcion: PLATFORM no lee `data`, su detalle es el aviso estatico
  // de que fallo la sync de cuentas. Sin la excepcion, un PLATFORM con `data`
  // vacio se queda sin flecha y el aviso deja de ser accesible.
  const hasDetails = type === "PLATFORM" || !isEmptyData(notification.data);
  const expandIcon = hasDetails ? (
    <ExpandMoreIcon data-testid="notification-expand-icon" />
  ) : null;

  const handleExpanded: AccordionProps["onChange"] = (e, expanded) => {
    setExpanded(expanded);
  };

  return (
    <ListItem disableGutters>
      <Accordion
        expanded={expanded}
        className={classes.accordion}
        onChange={handleExpanded}
      >
        <StyledAccordionSummary expandIcon={expandIcon}>
          <ListItemText
            primary={
              <>
                <PropertyLabel>
                  {new Date(createdAt).toLocaleString()}
                </PropertyLabel>
                <Grid container>
                  <Grid item lg={6}>
                    <PropertyLabel>{t("level")}:</PropertyLabel>
                    <PropertyData className={classes.data}>
                      {t(`notification.level.${level}`)}
                    </PropertyData>
                  </Grid>
                  <Grid item lg={6}>
                    <PropertyLabel>{t("type")}:</PropertyLabel>
                    <PropertyData className={classes.data}>
                      {t(`notification.type.${type}`)}
                    </PropertyData>
                  </Grid>
                </Grid>
              </>
            }
            secondary={
              <Grid container>
                <Grid item lg={6}>
                  <PropertyLabel>{t("id")}:</PropertyLabel>
                  <PropertyData className={classes.data}>{id}</PropertyData>
                </Grid>
                <Grid item lg={6}>
                  <PropertyLabel>{t("code")}:</PropertyLabel>
                  <CodeAndHelp notification={notification} />
                </Grid>
              </Grid>
            }
          />
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          {type === "MAIL" && (
            <EmailDetails
              notification={notification}
              expanded={expanded}
              onError={() => setExpanded(false)}
            />
          )}
          {type === "RULE" && (
            <RuleDetails
              notification={notification}
              expanded={expanded}
              onError={() => setExpanded(false)}
            />
          )}
          {type === "PLATFORM" && (
            <PlatformDetails notification={notification} />
          )}
          {type === "STORAGE" && <StorageDetails notification={notification} />}
        </StyledAccordionDetails>
      </Accordion>
    </ListItem>
  );
}

const PropertyLabel = ({ children }) => {
  return (
    <Typography color="textSecondary" component="span" variant="body2">
      {children}
    </Typography>
  );
};

const useStyles = makeStyles({
  accordion: { width: "100%" },
  data: {
    marginLeft: "5px",
    fontWeight: 500,
    display: "inline",
  },
});

function CodeAndHelp({ notification }: { notification: NotificationEvent }) {
  const { t } = useTranslation("notifications");
  const classes = useStyles();
  const { code } = notification;

  const descriptionHelp = t(`codeHelp.${code}.desc`);

  if (!descriptionHelp) {
    return <PropertyData className={classes.data}>{code}</PropertyData>;
  }

  return (
    <HtmlTooltip
      title={
        <>
          <Typography variant="subtitle2" style={{ marginBottom: ".5rem" }}>
            {t("codeHelp.desc")}
          </Typography>
          <Typography variant="body2" style={{ marginBottom: "1.5rem" }}>
            {t(`codeHelp.${code}.desc`)}
          </Typography>

          <Typography variant="subtitle2" style={{ marginBottom: ".5rem" }}>
            {t("codeHelp.action")}
          </Typography>
          <Typography variant="body2">
            {t(`codeHelp.${code}.action`)}
          </Typography>
        </>
      }
    >
      <Box display="inline-flex" gridGap="5px" alignItems="center">
        <PropertyData className={classes.data}>{code}</PropertyData>
        <HelpOutlineIcon color="primary" style={{ fontSize: "1rem" }} />
      </Box>
    </HtmlTooltip>
  );
}

const HtmlTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: "white",
    color: "rgba(0, 0, 0, 0.87)",
    border: "1px solid #dadde9",
    padding: "12px 15px",
  },
}))(Tooltip);

function isEmptyData(data: NotificationEvent["data"]) {
  return Object.keys(data).length === 0;
}
